<div class="min-h-screen bg-gray-50 dark:bg-gray-900 py-8" [class.dark]="themeService.isDark()">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

    <!-- Header -->
    <div class="text-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">
        Congress Trading Disclosures
      </h1>
      <p class="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
        Let's see what Congress is buying to get some ideas.
      </p>
    </div>

    <!-- Filters -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
         <!-- Group By Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Group By
          </label>
          <p-select
            [options]="groupByOptions"
            [(ngModel)]="selectedGroupBy"
            (onChange)="onGroupByChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="Select grouping"
            size="large"
            [checkmark]="true"
            class="w-full">
          </p-select>
        </div>

        <!-- Time Period Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Time Period
          </label>
          <p-select
            [options]="timePeriodOptions"
            [(ngModel)]="selectedTimePeriod"
            (onChange)="onTimePeriodChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="Select period"
            size="large"
            [checkmark]="true"
            class="w-full">
          </p-select>
        </div>

        <!-- Chamber Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Chamber
          </label>
          <p-select
            [options]="chamberOptions"
            [(ngModel)]="selectedChamber"
            (onChange)="onChamberChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="Select chamber"
            size="large"
            [checkmark]="true"
            class="w-full">
          </p-select>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    @if (loading()) {
      <app-progress-spinner [loadingText]="'Loading congressional trades...'"></app-progress-spinner>
    }

    <!-- Error State -->
    @if (error()) {
      <p-message severity="error" [text]="error()!"></p-message>
    }

    <!-- Content -->
    @if (!loading() && !error()) {

      <!-- Grouped Data Table -->
      @if (sortedGroupKeys().length > 0) {
        <div class="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <!-- Table Header -->
              <thead class="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th class="px-3 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {{ selectedGroupBy() === 'member' ? 'Member' : 'Ticker' }}
                  </th>
                </tr>
              </thead>

              <!-- Table Body -->
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <ng-container *ngFor="let groupKey of sortedGroupKeys(); let i = index; trackBy: trackByGroupKey">
                  <!-- Group Row -->
                  <tr class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td class="px-3 py-3 whitespace-nowrap">
                      <div class="flex items-center space-x-2">
                        <button
                          (click)="toggleGroupExpansion(groupKey)"
                          class="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                          pTooltip="{{ isGroupExpanded(groupKey) ? 'Collapse' : 'Expand' }}"
                          tooltipPosition="top">
                          <i class="fas fa-chevron-{{ isGroupExpanded(groupKey) ? 'up' : 'down' }}"></i>
                        </button>
                        @if (selectedGroupBy() === 'member') {
                          <div class="flex items-center space-x-2">
                            <div class="text-xl font-medium text-gray-900 dark:text-white">
                              {{ groupKey }} ({{ groupedDisclosures()[groupKey].length }})
                            </div>
                            <app-chamber-badge [chamber]="groupedDisclosures()[groupKey][0].chamber"></app-chamber-badge>
                          </div>
                        } @else {
                          <div class="flex items-center space-x-2">
                            <span class="rounded-md text-lg font-medium">({{ groupedDisclosures()[groupKey].length }})</span>
                            <a [routerLink]="['/stocks', groupKey]"
                              class="font-mono font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors">
                              <app-company-display [logoUrl]="groupedDisclosures()[groupKey][0].logoUrl" [ticker]="groupedDisclosures()[groupKey][0].ticker" [companyName]="groupedDisclosures()[groupKey][0].companyName"></app-company-display>
                            </a>
                          </div>
                        }
                      </div>
                      @if (selectedGroupBy() === 'member') {
                        <div class="text-sm text-gray-500 dark:text-gray-400 ml-6">
                          {{ groupedDisclosures()[groupKey][0].office }} -  {{ groupedDisclosures()[groupKey][0].district }}
                        </div>
                      }

                      <!-- Expanded Details (Inner Row) -->
                      @if (isGroupExpanded(groupKey)) {
                        <div class="mt-4 ml-6 border-l-2 border-gray-200 dark:border-gray-600 pl-4">
                          <div class="overflow-x-auto">
                            <table class="w-full text-sm bg-gray-50 dark:bg-gray-700 rounded-md">
                              <thead class="bg-gray-100 dark:bg-gray-600">
                                <tr>
                                  @if (selectedGroupBy() === 'member') {
                                    <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Ticker</th>
                                  } @else {
                                    <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Member</th>
                                  }
                                  <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Type</th>
                                  <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Transaction Date</th>
                                  <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Disclose Date</th>
                                  <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Asset</th>
                                  <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Comment</th>
                                  <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Amount</th>
                                  <th class="px-2 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">View</th>
                                </tr>
                              </thead>
                              <tbody class="divide-y divide-gray-200 dark:divide-gray-500">
                                <tr *ngFor="let disclosure of groupedDisclosures()[groupKey]; trackBy: trackByDisclosure"
                                    class="hover:bg-gray-100 dark:hover:bg-gray-600">
                                  @if (selectedGroupBy() === 'member') {
                                    <td class="px-2 py-2 whitespace-nowrap">
                                      <a [routerLink]="['/stocks', disclosure.ticker]"
                                         class="font-mono text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline">
                                        <app-company-display [logoUrl]="disclosure.logoUrl" [ticker]="disclosure.ticker" [companyName]="disclosure.companyName"></app-company-display>
                                         <!-- {{ disclosure.ticker }} -->
                                      </a>

                                    </td>
                                  } @else {
                                    <td class="px-2 py-2 whitespace-nowrap">
                                      <div class="flex items-left space-x-2">
                                           <div class="font-medium text-gray-900 dark:text-white">
                                              {{ disclosure.firstName }} {{ disclosure.lastName }}
                                            </div>
                                            <app-chamber-badge [chamber]="disclosure.chamber"></app-chamber-badge>
                                      </div>

                                      <div class="text-sm text-gray-500 dark:text-gray-400">
                                        {{ disclosure.office }} - {{ disclosure.district }}
                                      </div>
                                    </td>
                                  }
                                  <td class="px-2 py-2 whitespace-nowrap">
                                    <span class="px-1 py-0.5 rounded text-xs font-medium">
                                      {{ disclosure.type }}
                                    </span>
                                  </td>
                                  <td class="px-2 py-2 whitespace-nowrap">
                                    <span class="text-gray-900 dark:text-white">
                                      {{ disclosure.formattedTransactionDate }}
                                    </span>
                                  </td>
                                  <td class="px-2 py-2 whitespace-nowrap">
                                    <span class="text-gray-900 dark:text-white">
                                      {{ disclosure.formattedDisclosureDate }}
                                    </span>
                                  </td>
                                  <td class="px-2 py-2 whitespace-nowrap">
                                    <span class="text-gray-900 dark:text-white">
                                      {{ disclosure.assetType }}
                                    </span>
                                  </td>
                                  <td class="px-2 py-2 whitespace-nowrap">
                                    <span class="text-gray-900 dark:text-white">
                                      {{ disclosure.comment || 'N/A' }}
                                    </span>
                                  </td>

                                  <td class="px-2 py-2 whitespace-nowrap">
                                    <span class="font-mono text-gray-900 dark:text-white">
                                      {{ disclosure.formattedAmount }}
                                    </span>
                                  </td>
                                  <td class="px-2 py-2 whitespace-nowrap">
                                    @if(disclosure.link) {
                                      <span class="font-mono text-green dark:text-yellow">
                                        <a [href]="disclosure.link" target="_blank" rel="noopener noreferrer"><i class="fas fa-file-invoice"></i></a>
                                      </span>
                                    }
                                    @else {
                                      <span class="text-gray-500 dark:text-gray-400">N/A</span>
                                    }
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      }
                    </td>


                </ng-container>
              </tbody>
            </table>
          </div>
        </div>
      } @else {
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <i class="fas fa-search text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
          <h3 class="text-xl font-medium text-gray-900 dark:text-white mb-2">
            No Data Available
          </h3>
          <p class="text-gray-600 dark:text-gray-300">
            No congressional disclosures match your current filters
          </p>
        </div>
      }
    }
  </div>
</div>
